import {
  Component,
  Inject,
  Injector,
  OnDestroy,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgOtpInputModule } from 'ng-otp-input';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { ProfileSetupComponent } from '../profile-setup/profile-setup.component';
import {
  ForgotPayload,
  loginOtp,
  SignUpBody,
  User,
  ValidateAccount,
  VerifyOtp
} from '../../../models/auth.model';
import { OtpType, SignUpType } from '../../../core/constants/keys.constant';
import { Subject, takeUntil } from 'rxjs';
import { BaseClass } from '../../../shared/baseClass/baseClass';
import { AuthService } from '../../../services/auth.service';
import { CommonResponse } from '../../../models/common.model';
import { responseStatus } from '../../../core/constants/responseStatus.const';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'xplora-website-verification',
  standalone: true,
  imports: [TranslateModule, MatIconModule, MatDialogModule, NgOtpInputModule, CommonModule],
  templateUrl: './verification.component.html',
  styleUrl: './verification.component.scss'
})
export class VerificationComponent extends BaseClass implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('ngOtpInput') ngOtpInput: any;

  private destroy: Subject<boolean> = new Subject<boolean>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public intervalHandle: any;
  protected sendAgain = false;
  protected showTimerString = true;
  protected otpTimer = 60;
  private otp!: string;
  private deviceToken!: string;

  protected signUpType = SignUpType;
  protected guestId: string | null = null;

  constructor(
    injector: Injector,
    private _authService: AuthService,
    public dialogRef: MatDialogRef<VerificationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SignUpBody,
    public dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: object,
    private matDialogRef: MatDialogRef<VerificationComponent>
  ) {
    super(injector);
    this.startTimer();
    this._authService.deviceTokenSubject
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: string) => {
          this.deviceToken = res;
        }
      });
    if (isPlatformBrowser(this.platformId)) {
      if (localStorage.getItem(environment.guestId)) {
        this.guestId = localStorage.getItem(environment.guestId);
      }
    }
  }

  startTimer(): void {
    this.intervalHandle = setInterval(() => {
      if (this.otpTimer > 0) {
        if (this.sendAgain) this.sendAgain = false;
        this.otpTimer--;
      } else {
        this.sendAgain = true;
        this.showTimerString = false;
        clearInterval(this.intervalHandle);
      }
    }, 1000);
  }

  openResetPasswordModal(): void {
    const dialogRef = this.dialog.open(ResetPasswordComponent, {
      panelClass: ['modal', 'auth_modal'],
      data: { resetPassword: false }
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openProfileSetupModal(): void {
    this.dialog.open(ProfileSetupComponent, {
      panelClass: ['modal', 'auth_modal', 'setup_modal'],
      data: this.data
    });
    this.dialogRef.close();
  }

  sendOtpAgain(): void {
    this.showTimerString = true;
    this.otpTimer = 60;
    this.startTimer();

    this.spinnerService.addToLoader('sendOtpAgain');
    const payload: ForgotPayload = {
      [this.data.signUpType === this.signUpType.Email ? 'email' : 'phoneNo']:
        this.data.data.email ? this.data.data.email : this.data.data.phoneNo
    };

    if (this.data.signUpType === this.signUpType.Phone) {
      payload.dialCode = this.data.data.dialCode;
    }

    const url =
      this.data.otpType === OtpType.Verification ? this._authService.validateAccount(payload) : this._authService.sendOtp(payload);

    url.pipe(takeUntil(this.destroy)).subscribe({
      next: (res: CommonResponse) => {
        this.spinnerService.removeFromLoader('sendOtpAgain');
        if (res.statusCode === responseStatus.SUCCESS) {
          this.toasterService.successToastr(res.message);
        } else {
          this.toasterService.errToastr(res.message);
        }
      },
      error: () => {
        this.spinnerService.removeFromLoader('sendOtpAgain');
      }
    });
  }

  verifyOtp(): void {
    this.otp = this.ngOtpInput.currentVal;
    if (this.otp === undefined || this.otp.length !== 4) {
      this.toasterService.errToastr(
        this.translateService.instant('VaidateLength')
      );
      return;
    }

    if (this.otp.length === 4) {
      switch (this.data.otpType) {
      case OtpType.SignUp:
        this.handleApiHitForOtpVerification();
        break;
      case OtpType.Verification:
        this.handleApiHitForValidateAccount();
        break;
      case OtpType.Forgot:
        this.handleApiHitForForgotAccount();
        break;
      case OtpType.login:
        this.handleApiHitForLoginAccount();
        break;
      default:
        break;
      }
    } else {
      this.toasterService.errToastr(this.translateService.instant('AddOTP'));
    }
  }

  handleApiHitForValidateAccount(): void {
    const payload: ValidateAccount = {
      otp: this.otp,
      [this.data.signUpType === this.signUpType.Email ? 'email' : 'phoneNo']:
        this.data.data.email ? this.data.data.email : this.data.data.phoneNo
    };

    if (this.data.signUpType === this.signUpType.Phone) {
      payload.dialCode = this.data.data.dialCode;
    }

    this.spinnerService.addToLoader('validateAccount');
    this._authService
      .verifyValidate(payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => {
          this.spinnerService.removeFromLoader('validateAccount');
          
          // Check if this verification was triggered from trial flow
          if (this.data?.trialFlow && this.data?.membershipPlan) {
            this.dialogRef.close({
              status: responseStatus.SUCCESS,
              trialFlow: true,
              membershipPlan: this.data.membershipPlan,
              source: this.data.source
            });
          } else {
            this.dialogRef.close(responseStatus.SUCCESS);
          }
        },
        error: () => {
          this.spinnerService.removeFromLoader('validateAccount');
        }
      });
  }

  handleApiHitForForgotAccount(): void {
    const payload: ValidateAccount = {
      otp: this.otp,
      [this.data.signUpType === this.signUpType.Email ? 'email' : 'phoneNo']:
        this.data.data.email ? this.data.data.email : this.data.data.phoneNo
    };

    if (this.data.signUpType === this.signUpType.Phone) {
      payload.dialCode = this.data.data.dialCode;
    }

    this.spinnerService.addToLoader('forgotAccount');
    this._authService
      .verifyForgotPassword(payload)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: User) => {
          this.spinnerService.removeFromLoader('forgotAccount');
          this._authService.resetTokenSubject.next(
            res.accessToken ? res.accessToken : ''
          );
          this.openResetPasswordModal();
          
          // Check if this verification was triggered from trial flow
          if (this.data?.trialFlow && this.data?.membershipPlan) {
            this.dialogRef.close({
              status: responseStatus.SUCCESS,
              trialFlow: true,
              membershipPlan: this.data.membershipPlan,
              source: this.data.source
            });
          } else {
            this.dialogRef.close(responseStatus.SUCCESS);
          }
        },
        error: () => {
          this.spinnerService.removeFromLoader('forgotAccount');
        }
      });
  }

  handleApiHitForOtpVerification(): void {
    this.spinnerService.addToLoader('verifyOtp');

    const payload: VerifyOtp = {
      deviceToken: this.deviceToken,
      deviceType: 'WEB',
      otp: this.otp,
      type: OtpType.SignUp,
      [this.data.signUpType === this.signUpType.Email ? 'email' : 'phoneNo']:
        this.data.data.email ? this.data.data.email : this.data.data.phoneNo
    };

    if (this.data.signUpType === this.signUpType.Phone) {
      payload.dialCode = this.data.data.dialCode;
    }

    const query = this.guestId ? `guestId=${this.guestId}` : '';

    this._authService
      .verifyOtp(payload, query)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: User) => {
          this.spinnerService.removeFromLoader('verifyOtp');
          this._authService.profileDetails.next(res);
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(
              environment.storageKey,
              res.accessToken ? res.accessToken : ''
            );
          }
          this.openProfileSetupModal();
        },
        error: () => {
          this.spinnerService.removeFromLoader('verifyOtp');
        }
      });
  }

  handleApiHitForLoginAccount(): void {
    this.spinnerService.addToLoader('verifyOtp');

    const payload: loginOtp = {
      deviceType: 'WEB',
      otp: this.otp
    };
    if (isPlatformBrowser(this.platformId)) {
      payload.deviceToken = localStorage.getItem('fcmToken') || 'xxxxxxxxxx';
    }

    if (this.data.signUpType === this.signUpType.Phone) {
      payload.dialCode = this.data.data.dialCode;
      payload.phoneNo = this.data.data.phoneNo;
    }

    // Add membership_id if this OTP login is from trial flow
    if (this.data?.trialFlow && this.data?.membershipPlan?.id) {
      (payload as any).membership_id = this.data.membershipPlan.id;
    }

    const query = this.guestId ? `guestId=${this.guestId}` : '';
    this._authService
      .loginOtp(payload, query)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (res: User) => {
          this.spinnerService.removeFromLoader('verifyOtp');
          this._authService.profileDetails.next(res);
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(
              environment.storageKey,
              res.accessToken ? res.accessToken : ''
            );
          }
          this.matDialogRef.close(res);
        },
        error: () => {
          this.spinnerService.removeFromLoader('verifyOtp');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.unsubscribe();
  }
}
